import { ArrowRightLeft, AppWindow, Building2, ListChecks, MoreHorizontal } from 'lucide-react';
import { usePopover } from '@/components/apps/usePopover';
import { PassToForm } from './PassToForm';
import type { FileSummary } from './team-week';
import type { TodayEntry } from './today-launches';
import { RowIdentity } from './work-row-visuals';

const ROW_CLASS = 'rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 sm:rounded-none sm:border-0';

/** The manager passes one of the member's tasks to someone else (relais). */
const PassMenu = ({ topic, onDone }: { topic: Parameters<typeof PassToForm>[0]['topic']; onDone: () => void }) => {
  const menu = usePopover();
  return (
    <div ref={menu.ref} className="relative shrink-0">
      <button type="button" aria-label={`Options pour ${topic.title}`} aria-expanded={menu.open} onClick={() => menu.setOpen(!menu.open)}
        className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {menu.open && (
        <div className="absolute right-0 top-9 z-20 w-72 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]">
          <PassToForm topic={topic} onDone={() => { menu.setOpen(false); onDone(); }} />
        </div>
      )}
    </div>
  );
};

const EntryRow = ({ entry, onChange }: { entry: TodayEntry; onChange: () => void }) => {
  if (entry.kind === 'launch') {
    const count = entry.launch.recordIds.length;
    return (
      <article className={ROW_CLASS}>
        <div className="work-action-row">
          <div className="work-account min-w-0"><RowIdentity icon={ListChecks} label={entry.launch.flowTitle} /></div>
          <div className="work-menu" />
          <div className="work-title min-w-0"><h3 className="text-sm leading-5 text-domino-ink">Traiter {count} fiche{count > 1 ? 's' : ''}</h3></div>
        </div>
      </article>
    );
  }
  const { row } = entry.item;
  return (
    <article className={ROW_CLASS}>
      <div className="work-action-row">
        <div className="work-account min-w-0">
          <RowIdentity icon={row.kind === 'app' ? AppWindow : Building2} label={row.kind === 'app' ? row.app.flowTitle : row.ownerLabel} />
        </div>
        <div className="work-menu"><PassMenu topic={row.topic} onDone={onChange} /></div>
        <div className="work-title min-w-0"><h3 className="text-sm leading-5 text-domino-ink">{row.topic.title}</h3></div>
      </div>
    </article>
  );
};

/**
 * A member's file, read by their manager (team scope): what they see, without
 * their commands; the manager can pass a task to someone else.
 */
export const MemberFile = ({ name, summary, nameOf, onChange }: {
  name: string;
  summary: FileSummary;
  nameOf: (userId: string) => string;
  onChange: () => void;
}) => {
  if (summary.tasks === 0) return <p className="py-6 text-sm text-domino-inkSoft">Rien à traiter chez {name}.</p>;
  return (
    <section aria-label={`File de ${name}`} className="space-y-2 sm:space-y-0 sm:divide-y sm:divide-domino-cardBorder sm:rounded-lg sm:border sm:border-domino-cardBorder sm:bg-domino-white">
      {summary.incoming.map((handoff) => (
        <article key={handoff.id} className={ROW_CLASS}>
          <div className="work-action-row">
            <div className="work-account min-w-0"><RowIdentity icon={ArrowRightLeft} label={`De ${nameOf(handoff.fromUserId)}`} detail={handoff.contextLabel ?? undefined} /></div>
            <div className="work-menu" />
            <div className="work-title min-w-0"><h3 className="text-sm leading-5 text-domino-ink">{handoff.title}</h3></div>
          </div>
        </article>
      ))}
      {summary.entries.map((entry) => (
        <EntryRow key={entry.kind === 'launch' ? entry.launch.key : entry.item.row.key} entry={entry} onChange={onChange} />
      ))}
    </section>
  );
};
